/**
 * Enhanced Multi-Store Theme System
 * Supports complete store autonomy with dynamic module loading
 */

import { loadEnhancedTheme } from './utils/storeModuleLoader.js';

/**
 * Theme Registry with Enhanced Capabilities
 */
const themes = {
  default: {
    name: 'PAUL',
    id: 'default',
    description: 'Classic PAUL bakery theme',
    type: 'bakery',
    
    // Enhanced capabilities
    capabilities: {
      hasCustomComponents: false,
      hasCustomPages: false,
      hasCustomLayouts: false,
      hasCustomHooks: false,
      autonomyLevel: 'basic' // basic, partial, full
    },
    
    // Legacy loader for compatibility
    loader: () => import('./stores/default/tokens.js')
  },
  
  antic: {
    name: 'Antic',
    id: 'antic',
    description: 'Artisanal Antic bakery theme with complete autonomy',
    type: 'bakery',
    
    // Enhanced capabilities  
    capabilities: {
      hasCustomComponents: true,
      hasCustomPages: true,
      hasCustomLayouts: true,
      hasCustomHooks: true,
      autonomyLevel: 'full' // basic, partial, full
    },
    
    // Legacy loader for compatibility
    loader: () => import('./stores/antic/tokens.js')
  }
};

/**
 * Enhanced theme loading with module support
 * @param {string} themeId - Theme identifier
 * @returns {Promise<Object>} - Complete theme object with modules
 */
export const loadTheme = async (themeId) => {
  console.log(`🎨 Loading enhanced theme: ${themeId}`);
  
  if (!themes[themeId]) {
    console.error(`❌ Theme ${themeId} not found`);
    throw new Error(`Theme ${themeId} not found`);
  }
  
  try {
    // Try enhanced loading first
    console.log(`🔄 Attempting enhanced theme loading for: ${themeId}`);
    const enhancedTheme = await loadEnhancedTheme(themeId);
    
    // Merge with theme registry data and ensure ID is set
    const completeTheme = {
      ...themes[themeId],
      ...enhancedTheme,
      id: themeId, // Explicitly set the ID
      loadedAt: new Date().toISOString()
    };
    
    console.log(`✅ Enhanced theme ${themeId} loaded successfully with ID:`, completeTheme.id);
    return completeTheme;
    
  } catch (error) {
    console.warn(`⚠️ Enhanced theme loading failed for ${themeId}, using basic fallback:`, error.message);
    
    // Fallback to basic theme loading
    try {
      const themeModule = await themes[themeId].loader();
      const basicTheme = {
        ...themes[themeId],
        id: themeId, // Explicitly set the ID here too
        tokens: themeModule.default || themeModule,
        isBasicFallback: true,
        loadedAt: new Date().toISOString()
      };
      
      console.log(`✅ Basic theme ${themeId} loaded as fallback with ID:`, basicTheme.id);
      return basicTheme;
      
    } catch (fallbackError) {
      console.error(`❌ Both enhanced and basic theme loading failed for ${themeId}:`, fallbackError);
      throw fallbackError;
    }
  }
};

/**
 * Get available themes with their capabilities
 * @returns {Array} - Available themes
 */
export const getAvailableThemes = () => {
  return Object.values(themes).map(theme => ({
    id: theme.id,
    name: theme.name,
    description: theme.description,
    type: theme.type,
    capabilities: theme.capabilities || {
      hasCustomComponents: false,
      hasCustomPages: false,
      hasCustomLayouts: false,
      hasCustomHooks: false,
      autonomyLevel: 'basic'
    }
  }));
};

/**
 * Get default theme
 * @returns {Object} - Default theme object
 */
export const getDefaultTheme = () => {
  return themes.antic; // Changed from 'default' to 'antic' as per user preference
};

/**
 * Register a new theme (for dynamic theme creation)
 * @param {Object} themeConfig - Theme configuration
 */
export const registerTheme = (themeConfig) => {
  if (!themeConfig.id) {
    throw new Error('Theme ID is required');
  }
  
  themes[themeConfig.id] = {
    name: themeConfig.name || themeConfig.id,
    id: themeConfig.id,
    description: themeConfig.description || `${themeConfig.name} theme`,
    type: themeConfig.type || 'bakery',
    capabilities: themeConfig.capabilities || {
      hasCustomComponents: false,
      hasCustomPages: false,
      hasCustomLayouts: false,
      hasCustomHooks: false,
      autonomyLevel: 'basic'
    },
    loader: themeConfig.loader || (() => import(`./stores/${themeConfig.id}/tokens.js`)),
    ...themeConfig
  };
  
  console.log(`✅ Theme ${themeConfig.id} registered successfully`);
};

/**
 * Check if theme exists
 * @param {string} themeId - Theme identifier
 * @returns {boolean} - Whether theme exists
 */
export const hasTheme = (themeId) => {
  return !!themes[themeId];
};

export { themes };
export default themes;
